import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import { listSites } from '@/api/sites';
import { useAuthStore } from '@/store/authStore';

/**
 * Multi-site: a quick-switch dropdown for the Studio header and the Dashboard. Lets a tenant with
 * several sites hop between them without going back to "My Sites". Renders nothing for single-site
 * accounts. Switching sets the active site (→ X-Site-Id) and deep-links via ?site=; ActiveSiteSync
 * sees the new ?site and refetches everything once. (This used to invalidate the cache itself as
 * well, so every query on the page was fetched twice per switch.)
 */
export function SiteSwitcher({ basePath = '/studio', tone = 'light' }: {
  /** The page to reopen on the chosen site. */
  basePath?: string;
  /** 'dark' for a dark band (the Dashboard hero). */
  tone?: 'light' | 'dark';
} = {}): JSX.Element | null {
  const navigate = useNavigate();
  const activeSiteId = useAuthStore((s) => s.activeSiteId);
  const { data: sites = [] } = useQuery({
    queryKey: ['my-sites'],
    queryFn: listSites,
    staleTime: 60_000,
  });

  const active = useMemo(
    () => sites.find((s) => s.id === activeSiteId) ?? sites.find((s) => s.is_primary) ?? sites[0],
    [sites, activeSiteId],
  );

  // Single-site accounts don't need a switcher — the existing site name in the header is enough.
  if (sites.length <= 1) return null;

  return (
    <div className="relative flex items-center">
      <select
        aria-label="Switch site"
        value={active?.id ?? ''}
        onChange={(e) => {
          const id = e.target.value;
          if (!id || id === active?.id) return;
          useAuthStore.getState().setActiveSiteId(id);
          navigate(`${basePath}?site=${id}`);
        }}
        className={tone === 'dark'
          ? 'max-w-[220px] cursor-pointer appearance-none truncate rounded-full bg-white/10 py-0.5 pl-2.5 pr-6 text-xs font-medium text-brand-lavender ring-1 ring-white/10 focus:outline-none focus:ring-2 focus:ring-brand-lavender [&>option]:text-brand-midnight'
          : 'max-w-[200px] truncate rounded-md border border-brand-border bg-white py-1 pl-2 pr-6 text-xs font-semibold text-brand-midnight'}
      >
        {sites.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}{s.is_primary ? ' · primary' : ''}
          </option>
        ))}
      </select>
      <ChevronDown className={`pointer-events-none absolute right-1.5 h-3.5 w-3.5 ${tone === 'dark' ? 'text-brand-lavender' : 'text-brand-ink-soft'}`} />
    </div>
  );
}

export default SiteSwitcher;
